import { Injectable, NgZone, inject } from '@angular/core';
import { environment } from 'environments/environment';
import { ProfileService } from '@services/profile/profile.service';
import { PatientStateService } from '@services/patient/patient-state/patient-state.service';
import { OrderStateService } from '@services/ordering/order-state/order-state.service';
import { DashboardStateService } from '@services/dashboard-state/dashboard-state.service';

const LAST_ACTIVITY_KEY = 'smp:lastActivity';

type ActivityEvent =
    | 'mousemove' | 'mousedown' | 'keydown' | 'keypress'
    | 'wheel' | 'scroll' | 'touchstart' | 'click';

@Injectable({ providedIn: 'root' })
export class IdleService {
    private timeoutId: number | null = null;
    private initialized = false;
    private readonly timeoutMs = environment.inactivityTimeoutMs;

    readonly activityEvents: ActivityEvent[] = [
        'mousemove', 'mousedown', 'keydown', 'keypress',
        'wheel', 'scroll', 'touchstart', 'click'
    ];

    readonly boundActivityHandler = this.onActivity.bind(this);
    readonly boundStorageHandler = this.onStorage.bind(this);
    readonly profileService = inject(ProfileService);
    readonly patientStateService = inject(PatientStateService);
    readonly orderStateService = inject(OrderStateService);
    readonly dashboardStateService = inject(DashboardStateService);
    constructor(
        private zone: NgZone,
    ) { }

    // Start monitoring user activity
    start(timeoutOverrideMs?: number): void {
        if (this.initialized) return;
        this.initialized = true;

        const effectiveTimeout = timeoutOverrideMs ?? this.timeoutMs;

        this.zone.runOutsideAngular(() => {
            this.writeLastActivity(Date.now());

            this.activityEvents.forEach(evt =>
                document.addEventListener(evt, this.boundActivityHandler, { passive: true })
            );

            window.addEventListener('storage', this.boundStorageHandler);

            this.resetTimer(effectiveTimeout);
        });
    }

    // Stop monitoring—call on logout or component destroy. 
    stop(): void {
        if (!this.initialized) return;
        this.initialized = false;

        this.activityEvents.forEach(evt =>
            document.removeEventListener(evt, this.boundActivityHandler)
        );
        window.removeEventListener('storage', this.boundStorageHandler);

        if (this.timeoutId !== null) {
            window.clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }

    // ---- Internals ----

    private onActivity(): void {
        this.writeLastActivity(Date.now());
        this.resetTimer();
    }

    // Cross-tab sync: if another tab updates last activity, reset local timer.
    private onStorage(e: StorageEvent): void {
        if (e.key === LAST_ACTIVITY_KEY && e.newValue) {
            this.resetTimer();
        }
    }

    private resetTimer(timeoutOverrideMs?: number): void {
        const t = timeoutOverrideMs ?? this.timeoutMs;

        if (this.timeoutId !== null) {
            window.clearTimeout(this.timeoutId);
        }

        this.timeoutId = window.setTimeout(() => {
            const lastTs = this.readLastActivity();
            const now = Date.now();
            const elapsed = now - lastTs;

            if (elapsed >= this.timeoutMs) {
                // Idle period exceeded: run inside Angular and perform your logout flow
                this.zone.run(() => {
                    this.stop();
                    this.logoutDueToInactivity();
                });
            } else {
                // Someone was active (maybe other tab) — continue monitoring
                this.resetTimer();
            }
        }, t);
    }

    // Calls logoutDueToInactivity when user is idle 
    logoutDueToInactivity(): void {
        try {
            this.profileService.logoutUser().subscribe({
                error: (error: any) => console.error(error),
            });

            this.profileService.clearProfileCache();
            sessionStorage.clear();
            this.patientStateService.resetState();
            this.orderStateService.resetState();
            this.dashboardStateService.resetState();
            window.location.assign(environment.loginWebUrl);
        } catch (e) {
            console.error('Idle logout failed:', e);
            window.location.assign(environment.loginWebUrl);
        }
    }

    writeLastActivity(ts: number): void {
        try { localStorage.setItem(LAST_ACTIVITY_KEY, String(ts)); } catch { }
    }

    private readLastActivity(): number {
        try {
            const v = localStorage.getItem(LAST_ACTIVITY_KEY);
            return v ? Number(v) : 0;
        } catch { return 0; }
    }
}
